import { useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/features/toast";
import useAuthStore from "@/store/use-auth-store";
import { useTranslation } from "react-i18next";
import { AuthStage } from "@/components/auth/auth-stage";
import { establishSessionFromTokens } from "@/features/auth";

export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success, error } = useToast();
  const { setUser, setTokens } = useAuthStore();
  const { t } = useTranslation();
  const hasShownToast = useRef(false);
  const hasCalled = useRef(false);

  const handleOAuthCallback = useCallback(async () => {
    if (hasCalled.current) return;
    try {
      const token =
        searchParams.get("token") || searchParams.get("accessToken");
      const refreshToken = searchParams.get("refreshToken");
      const provider = searchParams.get("provider");
      const isNewUser = searchParams.get("isNewUser") === "true";
      const error = searchParams.get("error");

      if (error) {
        throw new Error(error);
      }

      if (!token) {
        throw new Error("Token eksik");
      }

      try {
        hasCalled.current = true;
        await establishSessionFromTokens({
          accessToken: token,
          refreshToken: refreshToken || null,
          isNewUser,
          provider: provider || "oauth",
          setTokens,
          setUser,
          // Toast'ı tek sefere indirgemek için sarmalıyoruz (StrictMode).
          success: (message, options) => {
            if (hasShownToast.current) return;
            hasShownToast.current = true;
            success(message, options);
          },
          t,
          navigate,
        });
      } catch (userError: any) {
        console.error("User data fetch error:", userError);
        hasCalled.current = false;
        throw new Error("Kullanıcı bilgileri alınamadı");
      }
    } catch (errorResponse: any) {
      console.error("OAuth callback error:", errorResponse);
      if (!hasShownToast.current) {
        error(errorResponse.message || t("error.somethingWentWrong"), {
          title: t("auth.loginError"),
        });
        hasShownToast.current = true;
      }
      navigate("/login");
    }
  }, [searchParams, navigate, success, error, setUser, t]);

  useEffect(() => {
    handleOAuthCallback();
  }, [handleOAuthCallback]);

  return (
    <AuthStage>
      <div className="flex flex-col items-center gap-6 text-center">
        <h2 className="text-2xl font-semibold text-text-primary dark:text-gray-100">{t("auth.processing")}</h2>
        <p className="text-sm text-muted-foreground">{t("auth.redirecting")}</p>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-foreground/20 border-t-foreground dark:border-white/20 dark:border-t-white" />
      </div>
    </AuthStage>
  );
}
